import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { cronJobAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft,
  RefreshCw,
  Play,
  Pause,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Activity,
  Zap,
  Calendar,
  ChevronDown,
  ChevronUp,
  Server,
  Code,
  FileCode,
  Info,
  Settings,
  Pencil,
  X,
  Save,
  Plus,
  Trash2,
  Globe,
  Link,
} from 'lucide-react';

const CronDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [executions, setExecutions] = useState([]);
  const [failedExecutions, setFailedExecutions] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [expandedExecution, setExpandedExecution] = useState(null);
  const [expandedJobSource, setExpandedJobSource] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [triggeringJob, setTriggeringJob] = useState(null);
  const [togglingJob, setTogglingJob] = useState(null);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  // Edit schedule modal state
  const [editingJob, setEditingJob] = useState(null);
  const [newCronExpression, setNewCronExpression] = useState('');
  const [savingSchedule, setSavingSchedule] = useState(false);

  // Create job modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creatingJob, setCreatingJob] = useState(false);
  const [deletingJob, setDeletingJob] = useState(null);
  const [newJob, setNewJob] = useState({
    name: '',
    description: '',
    cronExpression: '',
    scheduleDescription: '',
    url: '',
    method: 'POST',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statusRes, executionsRes, failedRes] = await Promise.all([
        cronJobAPI.getStatus(),
        cronJobAPI.getExecutions({ limit: 50 }),
        cronJobAPI.getFailedExecutions(20),
      ]);

      setStatus(statusRes.data);
      setExecutions(executionsRes.data.executions || []);
      setFailedExecutions(failedRes.data || []);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to fetch cron data:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleTriggerJob = async (jobId) => {
    setTriggeringJob(jobId);
    try {
      await cronJobAPI.triggerJob(jobId);
      // Wait a moment for job to start, then refresh
      setTimeout(fetchData, 1000);
    } catch (err) {
      console.error('Failed to trigger job:', err);
      setError(err.response?.data?.error || 'Failed to trigger job');
    } finally {
      setTriggeringJob(null);
    }
  };

  const handleToggleJob = async (jobId, currentEnabled) => {
    setTogglingJob(jobId);
    try {
      if (currentEnabled) {
        await cronJobAPI.stopJob(jobId);
      } else {
        await cronJobAPI.startJob(jobId);
      }
      await fetchData();
    } catch (err) {
      console.error('Failed to toggle job:', err);
      setError(err.response?.data?.error || 'Failed to toggle job');
    } finally {
      setTogglingJob(null);
    }
  };

  const handleEditSchedule = (job) => {
    setEditingJob(job);
    setNewCronExpression(job.cronExpression);
  };

  const handleSaveSchedule = async () => {
    if (!editingJob || !newCronExpression.trim()) return;

    setSavingSchedule(true);
    try {
      await cronJobAPI.updateSchedule(editingJob.id, newCronExpression.trim());
      setEditingJob(null);
      setNewCronExpression('');
      await fetchData();
    } catch (err) {
      console.error('Failed to update schedule:', err);
      setError(err.response?.data?.error || 'Failed to update schedule');
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleCloseEditModal = () => {
    setEditingJob(null);
    setNewCronExpression('');
  };

  const handleCreateJob = async () => {
    if (!newJob.name.trim() || !newJob.cronExpression.trim() || !newJob.url.trim()) {
      setError('Name, cron expression, and URL are required');
      return;
    }

    setCreatingJob(true);
    try {
      await cronJobAPI.createDynamicJob({
        name: newJob.name.trim(),
        description: newJob.description.trim(),
        cronExpression: newJob.cronExpression.trim(),
        scheduleDescription: newJob.scheduleDescription.trim(),
        jobType: 'webhook',
        config: {
          url: newJob.url.trim(),
          method: newJob.method,
        },
      });
      setShowCreateModal(false);
      setNewJob({
        name: '',
        description: '',
        cronExpression: '',
        scheduleDescription: '',
        url: '',
        method: 'POST',
      });
      await fetchData();
    } catch (err) {
      console.error('Failed to create job:', err);
      setError(err.response?.data?.error || 'Failed to create job');
    } finally {
      setCreatingJob(false);
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm(`Are you sure you want to delete the job "${jobId}"? This cannot be undone.`)) {
      return;
    }

    setDeletingJob(jobId);
    try {
      await cronJobAPI.deleteDynamicJob(jobId);
      await fetchData();
    } catch (err) {
      console.error('Failed to delete job:', err);
      setError(err.response?.data?.error || 'Failed to delete job');
    } finally {
      setDeletingJob(null);
    }
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setNewJob({
      name: '',
      description: '',
      cronExpression: '',
      scheduleDescription: '',
      url: '',
      method: 'POST',
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      success: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      error: { color: 'bg-red-100 text-red-800', icon: XCircle },
      running: { color: 'bg-blue-100 text-blue-800', icon: RefreshCw },
      timeout: { color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle },
      cancelled: { color: 'bg-gray-100 text-gray-800', icon: XCircle },
    };
    const variant = variants[status] || variants.cancelled;
    const Icon = variant.icon;

    return (
      <Badge className={`${variant.color} flex items-center gap-1`}>
        <Icon className={`h-3 w-3 ${status === 'running' ? 'animate-spin' : ''}`} />
        {status}
      </Badge>
    );
  };

  const formatDuration = (ms) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatRelativeTime = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const then = new Date(date);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  if (loading && !status) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-2" />
          <p className="text-gray-600">Loading cron dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Dashboard
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Cron Job Monitor</h1>
              <p className="text-sm text-gray-500">
                {lastRefresh && `Last updated: ${formatRelativeTime(lastRefresh)}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Job
            </Button>
            <Button variant="outline" onClick={fetchData} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" />
            <span>{error}</span>
            <Button variant="ghost" size="sm" onClick={() => setError(null)} className="ml-auto">
              Dismiss
            </Button>
          </div>
        )}

        {/* Status Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Service Status</p>
                  <p className="text-2xl font-bold">
                    {status?.isRunning ? (
                      <span className="text-green-600">Running</span>
                    ) : (
                      <span className="text-red-600">Stopped</span>
                    )}
                  </p>
                </div>
                <Activity className={`h-8 w-8 ${status?.isRunning ? 'text-green-600' : 'text-red-600'}`} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Jobs</p>
                  <p className="text-2xl font-bold">{status?.jobCount || 0}</p>
                </div>
                <Calendar className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Active Jobs</p>
                  <p className="text-2xl font-bold text-green-600">
                    {status?.jobs?.filter(j => j.enabled).length || 0}
                  </p>
                </div>
                <Play className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Recent Failures</p>
                  <p className="text-2xl font-bold text-red-600">{failedExecutions.length}</p>
                </div>
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Server Info */}
        {status?.serverInfo && (
          <Card className="mb-6">
            <CardContent className="pt-4">
              <div className="flex items-center gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Server className="h-4 w-4" />
                  <span>Host: {status.serverInfo.hostname}</span>
                </div>
                <div>PID: {status.serverInfo.pid}</div>
                <div>Node: {status.serverInfo.nodeVersion}</div>
                {status.startedAt && (
                  <div>Started: {formatDate(status.startedAt)}</div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex border-b mb-6">
          {['overview', 'executions', 'failures'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-medium capitalize ${
                activeTab === tab
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab}
              {tab === 'failures' && failedExecutions.length > 0 && (
                <Badge className="ml-2 bg-red-100 text-red-800">{failedExecutions.length}</Badge>
              )}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {status?.jobs?.map((job) => (
              <Card key={job.id} className={`${!job.enabled ? 'opacity-60' : ''}`}>
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        {job.id}
                        {!job.enabled && <Badge variant="secondary">Disabled</Badge>}
                        {(job.category === 'custom' || job.id?.startsWith('custom-')) && (
                          <Badge className="bg-purple-100 text-purple-800 flex items-center gap-1">
                            <Globe className="h-3 w-3" />
                            Webhook
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription>{job.description || 'No description'}</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTriggerJob(job.id)}
                        disabled={triggeringJob === job.id}
                      >
                        {triggeringJob === job.id ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <Zap className="h-4 w-4" />
                        )}
                        <span className="ml-1">Run Now</span>
                      </Button>
                      <Button
                        variant={job.enabled ? 'destructive' : 'default'}
                        size="sm"
                        onClick={() => handleToggleJob(job.id, job.enabled)}
                        disabled={togglingJob === job.id}
                      >
                        {togglingJob === job.id ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : job.enabled ? (
                          <Pause className="h-4 w-4" />
                        ) : (
                          <Play className="h-4 w-4" />
                        )}
                        <span className="ml-1">{job.enabled ? 'Disable' : 'Enable'}</span>
                      </Button>
                      {job.sourceFile && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setExpandedJobSource(expandedJobSource === job.id ? null : job.id)}
                          className="text-gray-600"
                        >
                          <Code className="h-4 w-4" />
                          <span className="ml-1">Source</span>
                          {expandedJobSource === job.id ? (
                            <ChevronUp className="h-3 w-3 ml-1" />
                          ) : (
                            <ChevronDown className="h-3 w-3 ml-1" />
                          )}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditSchedule(job)}
                        className="text-gray-600"
                      >
                        <Pencil className="h-4 w-4" />
                        <span className="ml-1">Edit</span>
                      </Button>
                      {(job.category === 'custom' || job.id?.startsWith('custom-')) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteJob(job.id)}
                          disabled={deletingJob === job.id}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          {deletingJob === job.id ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                          <span className="ml-1">Delete</span>
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Schedule</p>
                      <p className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                        {job.cronExpression}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Last Run</p>
                      <p>{formatRelativeTime(job.lastRun)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Last Status</p>
                      {job.lastRunStatus ? getStatusBadge(job.lastRunStatus) : <span className="text-gray-400">-</span>}
                    </div>
                    <div>
                      <p className="text-gray-500">Duration</p>
                      <p>{formatDuration(job.lastRunDuration)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Next Run</p>
                      <p>{job.nextRun ? formatDate(job.nextRun) : '-'}</p>
                    </div>
                  </div>

                  <div className="flex gap-6 mt-4 pt-4 border-t text-sm">
                    <div>
                      <span className="text-gray-500">Total runs: </span>
                      <span className="font-medium">{job.runCount || 0}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Errors: </span>
                      <span className={`font-medium ${job.errorCount > 0 ? 'text-red-600' : ''}`}>
                        {job.errorCount || 0}
                      </span>
                    </div>
                    {job.lastError && (
                      <div className="text-red-600">
                        <span className="text-gray-500">Last error: </span>
                        <span>{job.lastError.message}</span>
                      </div>
                    )}
                  </div>

                  {/* Source Code Info - Collapsible */}
                  {expandedJobSource === job.id && job.sourceFile && (
                    <div className="mt-4 pt-4 border-t bg-slate-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <FileCode className="h-4 w-4 text-blue-600" />
                        <span className="font-medium text-gray-700">Source Code Details</span>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">File</p>
                          <code className="text-sm bg-gray-100 px-2 py-1 rounded block font-mono text-blue-700">
                            {job.sourceFile}
                          </code>
                        </div>

                        {job.sourceFunction && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Function</p>
                            <code className="text-sm bg-gray-100 px-2 py-1 rounded block font-mono text-purple-700">
                              {job.sourceFunction}
                            </code>
                          </div>
                        )}

                        {job.sourceDescription && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">What It Does</p>
                            <p className="text-sm text-gray-700 bg-gray-100 px-3 py-2 rounded leading-relaxed">
                              {job.sourceDescription}
                            </p>
                          </div>
                        )}

                        {job.category && (
                          <div className="pt-2">
                            <Badge variant="outline" className="text-xs">
                              Category: {job.category}
                            </Badge>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Executions Tab */}
        {activeTab === 'executions' && (
          <Card>
            <CardHeader>
              <CardTitle>Execution History</CardTitle>
              <CardDescription>Recent cron job executions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {executions.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No executions found</p>
                ) : (
                  executions.map((exec) => (
                    <div
                      key={exec.runId}
                      className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => setExpandedExecution(expandedExecution === exec.runId ? null : exec.runId)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {getStatusBadge(exec.status)}
                          <span className="font-medium">{exec.jobId}</span>
                          <Badge variant="outline">{exec.triggeredBy}</Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <div className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {formatDate(exec.startedAt)}
                          </div>
                          <span>{formatDuration(exec.duration)}</span>
                          {expandedExecution === exec.runId ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </div>
                      </div>

                      {expandedExecution === exec.runId && (
                        <div className="mt-4 pt-4 border-t text-sm space-y-2">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <span className="text-gray-500">Run ID: </span>
                              <span className="font-mono text-xs">{exec.runId}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Triggered By: </span>
                              <span>{exec.triggeredBy}</span>
                              {exec.triggeredByUser && ` (${exec.triggeredByUser})`}
                            </div>
                            <div>
                              <span className="text-gray-500">Started: </span>
                              <span>{new Date(exec.startedAt).toLocaleString()}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Completed: </span>
                              <span>{exec.completedAt ? new Date(exec.completedAt).toLocaleString() : '-'}</span>
                            </div>
                          </div>

                          {exec.result && (
                            <div className="mt-2 p-2 bg-green-50 rounded">
                              <p className="text-gray-500 text-xs mb-1">Result:</p>
                              <pre className="text-xs overflow-auto">
                                {JSON.stringify(exec.result, null, 2)}
                              </pre>
                            </div>
                          )}

                          {exec.error && (
                            <div className="mt-2 p-2 bg-red-50 rounded">
                              <p className="text-gray-500 text-xs mb-1">Error:</p>
                              <p className="text-red-700 font-medium">{exec.error.message}</p>
                              {exec.error.stack && (
                                <pre className="text-xs text-red-600 mt-2 overflow-auto max-h-40">
                                  {exec.error.stack}
                                </pre>
                              )}
                            </div>
                          )}

                          {exec.serverInfo && (
                            <div className="text-xs text-gray-400">
                              Server: {exec.serverInfo.hostname} (PID: {exec.serverInfo.pid})
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Failures Tab */}
        {activeTab === 'failures' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-600" />
                Failed Executions
              </CardTitle>
              <CardDescription>Recent job failures requiring attention</CardDescription>
            </CardHeader>
            <CardContent>
              {failedExecutions.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-2" />
                  <p className="text-gray-600">No recent failures</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {failedExecutions.map((exec) => (
                    <div key={exec.runId} className="border border-red-200 rounded-lg p-4 bg-red-50">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{exec.jobId}</span>
                          <Badge variant="outline">{exec.triggeredBy}</Badge>
                        </div>
                        <span className="text-sm text-gray-500">{formatDate(exec.startedAt)}</span>
                      </div>
                      <div className="text-red-700">
                        <p className="font-medium">{exec.error?.message || 'Unknown error'}</p>
                        {exec.error?.stack && (
                          <pre className="text-xs mt-2 p-2 bg-red-100 rounded overflow-auto max-h-32">
                            {exec.error.stack}
                          </pre>
                        )}
                      </div>
                      <div className="mt-2 text-xs text-gray-500">
                        Duration: {formatDuration(exec.duration)} | Run ID: {exec.runId}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Edit Schedule Modal */}
        {editingJob && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold">Edit Schedule</h3>
                <Button variant="ghost" size="sm" onClick={handleCloseEditModal}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Job ID</p>
                  <p className="font-medium">{editingJob.id}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Description</p>
                  <p className="text-gray-700">{editingJob.description || 'No description'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Current Schedule</p>
                  <code className="text-sm bg-gray-100 px-2 py-1 rounded block font-mono">
                    {editingJob.cronExpression}
                  </code>
                </div>
                <div>
                  <label className="text-sm text-gray-500 mb-1 block">New Cron Expression</label>
                  <input
                    type="text"
                    value={newCronExpression}
                    onChange={(e) => setNewCronExpression(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 0 8 * * *"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Format: minute hour day month weekday (e.g., "0 8 * * *" = 8:00 AM daily)
                  </p>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg text-sm">
                  <p className="font-medium text-blue-800 mb-2">Common schedules:</p>
                  <ul className="text-blue-700 space-y-1 text-xs">
                    <li><code className="bg-blue-100 px-1 rounded">0 8 * * *</code> - 8:00 AM daily</li>
                    <li><code className="bg-blue-100 px-1 rounded">0 18 * * *</code> - 6:00 PM daily</li>
                    <li><code className="bg-blue-100 px-1 rounded">0 6,18 * * *</code> - 6:00 AM and 6:00 PM</li>
                    <li><code className="bg-blue-100 px-1 rounded">0 * * * *</code> - Every hour</li>
                    <li><code className="bg-blue-100 px-1 rounded">*/30 * * * *</code> - Every 30 minutes</li>
                  </ul>
                </div>
              </div>
              <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
                <Button variant="outline" onClick={handleCloseEditModal} disabled={savingSchedule}>
                  Cancel
                </Button>
                <Button onClick={handleSaveSchedule} disabled={savingSchedule || !newCronExpression.trim()}>
                  {savingSchedule ? (
                    <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Schedule
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Create Job Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Globe className="h-5 w-5 text-blue-600" />
                  Create Webhook Job
                </h3>
                <Button variant="ghost" size="sm" onClick={handleCloseCreateModal}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Job Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newJob.name}
                    onChange={(e) => setNewJob({ ...newJob, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Daily Report Email"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Description
                  </label>
                  <input
                    type="text"
                    value={newJob.description}
                    onChange={(e) => setNewJob({ ...newJob, description: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Sends daily summary report to admin"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Cron Expression <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newJob.cronExpression}
                    onChange={(e) => setNewJob({ ...newJob, cronExpression: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 0 8 * * *"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Format: minute hour day month weekday
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Schedule Description
                  </label>
                  <input
                    type="text"
                    value={newJob.scheduleDescription}
                    onChange={(e) => setNewJob({ ...newJob, scheduleDescription: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Daily at 8:00 AM Pacific"
                  />
                </div>
                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                    <Link className="h-4 w-4" />
                    Webhook Configuration
                  </p>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-600 mb-1 block">
                        URL <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="url"
                        value={newJob.url}
                        onChange={(e) => setNewJob({ ...newJob, url: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="https://example.com/webhook"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600 mb-1 block">HTTP Method</label>
                      <select
                        value={newJob.method}
                        onChange={(e) => setNewJob({ ...newJob, method: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="GET">GET</option>
                        <option value="POST">POST</option>
                        <option value="PUT">PUT</option>
                        <option value="PATCH">PATCH</option>
                        <option value="DELETE">DELETE</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg text-sm">
                  <p className="font-medium text-blue-800 mb-2">Common schedules:</p>
                  <ul className="text-blue-700 space-y-1 text-xs">
                    <li><code className="bg-blue-100 px-1 rounded">0 8 * * *</code> - 8:00 AM daily</li>
                    <li><code className="bg-blue-100 px-1 rounded">0 18 * * *</code> - 6:00 PM daily</li>
                    <li><code className="bg-blue-100 px-1 rounded">0 6,18 * * *</code> - 6:00 AM and 6:00 PM</li>
                    <li><code className="bg-blue-100 px-1 rounded">0 * * * *</code> - Every hour</li>
                    <li><code className="bg-blue-100 px-1 rounded">*/30 * * * *</code> - Every 30 minutes</li>
                    <li><code className="bg-blue-100 px-1 rounded">0 8 * * 1-5</code> - 8:00 AM weekdays</li>
                  </ul>
                </div>
              </div>
              <div className="flex justify-end gap-2 p-4 border-t bg-gray-50 sticky bottom-0">
                <Button variant="outline" onClick={handleCloseCreateModal} disabled={creatingJob}>
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateJob}
                  disabled={creatingJob || !newJob.name.trim() || !newJob.cronExpression.trim() || !newJob.url.trim()}
                >
                  {creatingJob ? (
                    <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  Create Job
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CronDashboard;
